import * as Sentry from "@sentry/react";

import { UserEventType, createUserEvent } from "api/userEvents";

interface BulkDeleteCounts {
  shapefileIds: number;
  projectShapefileIds: number;
  folderIds: number;
  orgFolderIds: number;
  systemFolderIds: number;
}

/**
 * Custom hook for tracking shapefile management events
 * Provides specialized tracking for manage mode, bulk deletion, and context menu usage
 */
export function useShapefileTracking() {
  /**
   * Track entering manage mode
   */
  const trackManageModeEntered = (source: "button-click" | "context-menu") => {
    try {
      // Add Sentry breadcrumb for manage mode entry
      Sentry.addBreadcrumb({
        message: `User entered manage mode via ${source}`,
        level: "info",
        category: "user_interaction",
        data: {
          action: "enter_manage_mode",
          source,
          timestamp: new Date().toISOString()
        }
      });

      // Create user event
      createUserEvent({
        eventType: UserEventType.ManageModeEntered,
        context: {
          manageModeAction: source
        }
      });
    } catch (error) {
      // Silently handle tracking errors - no console logs in production
    }
  };

  /**
   * Track bulk delete operations with detailed counts
   */
  const trackBulkDelete = (counts: BulkDeleteCounts) => {
    try {
      const totalItems = Object.values(counts).reduce((sum, count) => sum + count, 0);

      // Add Sentry breadcrumb with deletion details
      Sentry.addBreadcrumb({
        message: "Bulk delete initiated",
        category: "shapefile_management",
        level: "info",
        data: {
          totalItems,
          ...counts,
          timestamp: new Date().toISOString()
        }
      });

      // Send analytics event with detailed counts
      createUserEvent({
        eventType: UserEventType.ShapefileBulkDelete,
        context: {
          bulkDeleteCounts: counts
        }
      });
    } catch (error) {
      // Silently handle tracking errors
    }
  };

  /**
   * Track context menu usage in manage mode
   */
  const trackContextMenuUsage = (action: string) => {
    try {
      // Add Sentry breadcrumb for context menu usage
      Sentry.addBreadcrumb({
        message: `Context menu action: ${action}`,
        category: "shapefile_management",
        level: "info",
        data: {
          action,
          timestamp: new Date().toISOString()
        }
      });

      // Send analytics event
      createUserEvent({
        eventType: UserEventType.ManageModeContextMenu,
        context: {
          contextMenuAction: action
        }
      });
    } catch (error) {
      // Silently handle tracking errors
    }
  };

  return {
    trackManageModeEntered,
    trackBulkDelete,
    trackContextMenuUsage
  };
}

export type { BulkDeleteCounts };
